import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Trash2, Plus } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { API, FIELD_LABELS, NUMERIC_FIELDS, OPERATOR_LABELS, errorText } from './constants';
import { Pick, RulePolicyFields } from './RulePolicyFields';

const POLICY = { action: 'require_approval', approval_mode: 'single', min_approvals: 1, approver_user_ids: [], levels: [],
  allow_exception: false, exception_role: 'super_admin' };
const EMPTY = { name: '', description: '', approver_role: 'admin', severity: 'medium', expires_in_hours: 72, ...POLICY,
  conditions: [{ field: 'amount', operator: 'gt', value: '' }] };

const toForm = (rule) => (rule ? { ...POLICY, ...rule, conditions: rule.conditions.map((c) => ({ ...c, value: Array.isArray(c.value) ? c.value.join(', ') : String(c.value) })) } : EMPTY);

const toPayload = (f) => ({
  name: f.name, description: f.description, approver_role: f.approver_role, severity: f.severity,
  expires_in_hours: Number(f.expires_in_hours), is_active: f.is_active ?? true,
  action: f.action, approval_mode: f.approval_mode, min_approvals: Number(f.min_approvals) || 1,
  approver_user_ids: f.approver_user_ids, levels: f.levels.map((l) => ({ role: l.role, min_approvals: Number(l.min_approvals) || 1 })),
  allow_exception: f.allow_exception, exception_role: f.exception_role,
  conditions: f.conditions.map((c) => ({
    field: c.field, operator: c.operator,
    value: c.operator === 'in' ? c.value.split(',').map((s) => s.trim()).filter(Boolean)
      : NUMERIC_FIELDS.includes(c.field) ? Number(c.value) : c.value,
  })),
});

const ConditionRow = ({ cond, idx, onChange, onRemove }) => (
  <div className="grid grid-cols-[1fr_110px_1fr_40px] gap-2" data-testid={`rule-condition-${idx}`}>
    <Pick value={cond.field} onChange={(v) => onChange({ ...cond, field: v })} options={FIELD_LABELS} testId={`rule-condition-field-${idx}`} />
    <Pick value={cond.operator} onChange={(v) => onChange({ ...cond, operator: v })} options={OPERATOR_LABELS} testId={`rule-condition-operator-${idx}`} />
    <Input value={cond.value} onChange={(e) => onChange({ ...cond, value: e.target.value })} placeholder="Valor (lista: a, b)" data-testid={`rule-condition-value-${idx}`} />
    <Button type="button" variant="ghost" size="icon" onClick={onRemove} data-testid={`rule-condition-remove-${idx}`}><Trash2 className="h-4 w-4" /></Button>
  </div>
);

const RuleFormDialog = ({ companyId, rule, onClose }) => {
  const qc = useQueryClient();
  const [form, setForm] = useState(toForm(rule));
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setCond = (i, c) => set('conditions', form.conditions.map((x, j) => (j === i ? c : x)));
  const save = useMutation({
    mutationFn: () => (rule
      ? axios.put(`${API}/companies/${companyId}/institutional-rules/${rule.id}`, toPayload(form))
      : axios.post(`${API}/companies/${companyId}/institutional-rules`, toPayload(form))),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['v2-rules', companyId] }); toast.success(rule ? 'Nueva versión guardada' : 'Regla creada'); onClose(); },
    onError: (e) => toast.error(errorText(e)),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="rule-form-dialog">
        <DialogHeader><DialogTitle>{rule ? `Editar regla (creará v${rule.version + 1})` : 'Nueva regla institucional'}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Input value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="Nombre de la regla" data-testid="rule-name-input" />
          <Textarea value={form.description} onChange={(e) => set('description', e.target.value)} placeholder="Descripción / política institucional" data-testid="rule-description-input" />
          <RulePolicyFields companyId={companyId} form={form} set={set} />
          <p className="text-xs font-medium text-slate-500">Condiciones (todas deben cumplirse)</p>
          {form.conditions.map((c, i) => (
            <ConditionRow key={i} idx={i} cond={c} onChange={(nc) => setCond(i, nc)} onRemove={() => set('conditions', form.conditions.filter((_, j) => j !== i))} />
          ))}
          <Button type="button" variant="outline" size="sm" onClick={() => set('conditions', [...form.conditions, { field: 'risk_level', operator: 'in', value: '' }])} data-testid="rule-add-condition-btn"><Plus className="mr-1 h-4 w-4" />Condición</Button>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="rule-cancel-btn">Cancelar</Button>
          <Button onClick={() => save.mutate()} disabled={save.isPending} data-testid="rule-save-btn">{save.isPending ? 'Guardando…' : 'Guardar'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RuleFormDialog;
